<script setup lang="ts">
import { programService } from '@/api/services/program-service'
import BaseLayout from '@/components/base/BaseLayout.vue'
import type { FilterOption } from '@/types/filter.types'
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ClassListSection from '../components/ClassListSection.vue'
import ProgramFilterSection from '../components/ProgramFilterSection.vue'
import VideoListSection from '../components/VideoListSection.vue'

const router = useRouter()
const route = useRoute()

const programs = ref<FilterOption[]>([])
const selectedProgram = ref<FilterOption | undefined>(undefined)
const selectedClass = ref<FilterOption | undefined>(undefined)
const classes = ref<FilterOption[]>([])
const filters = ref({
  level: (route.query.level as string) || 'all',
  sort: (route.query.sort as string) || 'latest',
})

// 클래스 목록 가져오기
const fetchClasses = async (programIdx: number) => {
  try {
    const response = await programService.getProgramCourses(programIdx)

    if (response.length > 1) {
      classes.value = [{ idx: 0, name: '모든 클래스', data: 'all' }, ...response]
    } else {
      classes.value = [{ idx: 0, name: '모든 클래스', data: 'all' }]
    }

    const classId = route.query.class

    // URL에 클래스 ID가 없는 경우에만 첫 번째 클래스 선택
    if ((!classId || classId === '0') && !selectedClass.value) {
      handleClassSelect(classes.value[0])
    } else if (classId) {
      // URL에 클래스 ID가 있는 경우 해당 클래스 찾아서 선택
      const selectedClassOption = classes.value.find((c) => c.idx === Number(classId))
      if (selectedClassOption) {
        selectedClass.value = selectedClassOption
      }
    }
  } catch (error) {
    console.error('클래스 목록을 가져오는 중 오류 발생:', error)
  }
}

const handleProgramSelect = (programOption: FilterOption) => {
  selectedProgram.value = programOption
  selectedClass.value = undefined // 프로그램이 변경되면 선택된 클래스 초기화

  // URL 쿼리 파라미터 업데이트 (class 파라미터 제거)
  const query = { ...route.query, class: 0 }
  router.replace({
    query: {
      ...query,
      program: programOption.idx,
    },
  })

  // 클래스 목록 가져오기
  fetchClasses(programOption.idx)
}

const handleClassSelect = (classOption: FilterOption) => {
  selectedClass.value = classOption
  // URL 쿼리 파라미터 업데이트
  router.replace({
    query: {
      ...route.query,
      class: classOption.idx,
    },
  })
}

const handleFilterChange = (newFilters: { level: string | number; sort: string | number }) => {
  if (typeof newFilters.level === 'string' && typeof newFilters.sort === 'string') {
    filters.value = newFilters as { level: string; sort: string }
    // URL 쿼리 파라미터 업데이트
    router.replace({
      query: {
        ...route.query,
        ...newFilters,
      },
    })
  }
}

onMounted(async () => {
  programs.value = await programService.getPrograms()
  const programId = route.query.program

  if (programId) {
    const program = programs.value.find((p) => p.idx === Number(programId))
    if (program) {
      selectedProgram.value = program
      await fetchClasses(program.idx)
    }
  } else if (programs.value.length > 0) {
    selectedProgram.value = programs.value[0]
    await fetchClasses(programs.value[0].idx)
  }
})
</script>

<template>
  <div>
    <BaseLayout>
      <ProgramFilterSection
        :programs="programs"
        :selected-program="selectedProgram"
        :filters="filters"
        @program-select="handleProgramSelect"
        @filter-change="handleFilterChange"
      />
      <ClassListSection
        :classes="classes"
        :selected-program="selectedProgram"
        :selected-class="selectedClass"
        @class-select="handleClassSelect"
      />
      <VideoListSection
        :selected-program="selectedProgram"
        :selected-class="selectedClass"
        :filters="filters"
      />
    </BaseLayout>
  </div>
</template>
